import React, { useState, useEffect, useCallback } from 'react';
import { 
    Cpu, 
    Copy, 
    Trash2, 
    RefreshCw, 
    Clock, 
    HardDrive,
    ExternalLink,
    AlertTriangle,
    CheckCircle2,
    Loader2
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import { workspaceApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useToast } from '../../context/ToastContext';
import './ActiveDevicesSettings.css';

function ActiveDevicesSettings() {
    const { currentWorkspace } = useWorkspace();
    const workspaceId = currentWorkspace?.id;
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [killing, setKilling] = useState(null);
    const [confirmKill, setConfirmKill] = useState(null);
    const { showToast } = useToast();

    const fetchDevices = useCallback(async () => {
        if (!workspaceId) return;
        try {
            const response = await workspaceApi.getDevices(workspaceId);
            setDevices(response.data.devices);
        } catch (error) {
            console.error('Fetch devices error:', error);
            showToast('Failed to fetch active devices', 'error');
        } finally {
            setLoading(false);
        }
    }, [workspaceId, showToast]);

    useEffect(() => {
        fetchDevices();
        const interval = setInterval(fetchDevices, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, [fetchDevices]);

    const handleCopyId = (id) => {
        navigator.clipboard.writeText(id);
        showToast('Device ID copied to clipboard');
    };

    const handleKillDevice = async (deviceId) => {
        setKilling(deviceId);
        try {
            await workspaceApi.killDevice(workspaceId, deviceId);
            showToast('Termination triggered for emulator');
            setConfirmKill(null);
            fetchDevices();
        } catch (error) {
            console.error('Kill device error:', error);
            showToast(error.response?.data?.error || 'Failed to terminate device', 'error');
        } finally {
            setKilling(null);
        }
    };

    const formatUptime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days}d ${hours % 24}h`;
        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
        return `${seconds}s`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading && devices.length === 0) {
        return (
            <div className="devices-loading">
                <Loader2 className="animate-spin" size={32} />
                <p>Loading workspace devices...</p>
            </div>
        );
    }

    return (
        <div className="active-devices-settings">
            <header className="settings-header">
                <div className="header-title-row">
                    <h1>Active Devices</h1>
                    <button className="btn-refresh" onClick={() => { setLoading(true); fetchDevices(); }}>
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
                <p className="settings-description">
                    Manage the virtualization instances currently allocated to this workspace. 
                    Emulators are automatically terminated after 15 minutes of inactivity.
                </p>
            </header>

            <div className="devices-table-container">
                <table className="devices-table">
                    <thead>
                        <tr>
                            <th>Device ID</th>
                            <th>Status</th>
                            <th>Platform & Profile</th>
                            <th>Installed App</th>
                            <th>Created At</th>
                            <th>Uptime</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {devices.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="empty-state">
                                    <Cpu size={48} />
                                    <p>No active devices found for this workspace.</p>
                                </td>
                            </tr>
                        ) : (
                            devices.map((device) => (
                                <tr key={device.id}>
                                    <td className="device-id-cell">
                                        <code title={device.id}>{device.id.slice(0, 8)}...</code>
                                        <button className="btn-icon" onClick={() => handleCopyId(device.id)}>
                                            <Copy size={14} />
                                        </button>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${device.status.toLowerCase()}`}>
                                            {device.status === 'AVAILABLE' && <CheckCircle2 size={12} />}
                                            {device.status === 'PROVISIONING' && <Loader2 size={12} className="animate-spin" />}
                                            {device.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="profile-info">
                                            <span className="platform-tag">{device.platform}</span>
                                            <span className="profile-name">{device.deviceProfile}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="app-info">
                                            <HardDrive size={14} />
                                            <span>{device.installedApp}</span>
                                        </div>
                                    </td>
                                    <td className="date-cell">
                                        {formatDate(device.createdAt)}
                                    </td>
                                    <td className="uptime-cell">
                                        <div className="uptime-info">
                                            <Clock size={14} />
                                            <span>{formatUptime(device.uptimeMs)}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <button 
                                            className="btn-kill"
                                            onClick={() => setConfirmKill(device.id)}
                                            disabled={killing === device.id}
                                        >
                                            <Trash2 size={16} />
                                            <span>Kill</span>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Confirmation Modal */}
            {confirmKill && (
                <div className="modal-overlay">
                    <div className="confirmation-modal">
                        <div className="modal-header">
                            <AlertTriangle className="warning-icon" />
                            <h2>Kill Emulator?</h2>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to kill this emulator?</p>
                            <p className="warning-text">
                                Killing it will cause any tests currently running on it to end immediately.
                                This action cannot be undone.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setConfirmKill(null)}>Cancel</button>
                            <button 
                                className="btn-danger" 
                                onClick={() => handleKillDevice(confirmKill)}
                                disabled={killing !== null}
                            >
                                {killing ? 'Killing...' : 'Yes, kill emulator'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ActiveDevicesSettings;
